import React, { useState, useCallback, useEffect, useMemo, memo } from "react";
import {
  Layout,
  Calendar,
  Button,
  Modal,
  DatePicker,
  List,
  Upload,
  Form,
  Input,
  Space,
  message,
  Select,
  Spin,
  Popconfirm,
  Flex,
  Avatar,
  Tag,
  Radio,
  Typography,
  Divider,
  Card,
  Result,
  Alert,
  Tooltip,
  Row, Col,
  Segmented,
  Slider,
  Empty,
} from "antd";
import {
  PlusOutlined,
  InboxOutlined,
  DeleteOutlined,
  DragOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
  FileImageOutlined,
  SplitCellsOutlined,
  PictureOutlined, PlaySquareOutlined, BorderOutlined,
  SendOutlined,
  WarningOutlined,
  SettingOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  CheckCircleFilled,
  CommentOutlined,
  ClockCircleOutlined,
  CameraOutlined,
  UploadOutlined,
  SaveOutlined,
  BookOutlined,
  EditOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { fireDB } from "../../funcs/firebase";
import { getDocs, collection, doc, getDoc, setDoc, deleteDoc, serverTimestamp, addDoc, query, where, updateDoc } from "firebase/firestore";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Sider from "../../comps/sider/main";
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import UploadFileToCF from "./mediaUpload";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import callAPI from "../../comps/fetch/main";
import './main.css'
import AccessOrNot from "../../funcs/accessOrNot";
import CrownReturn from "../../comps/crownPremium/main";
// Initialize the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const { Content } = Layout;
const RightSider = Layout.Sider;
const { Dragger } = Upload;
const { TextArea } = Input;

const { Title, Text } = Typography;


// Memoized video preview component with lazy loading
const iconStyle = {
  fontSize: '24px',
  marginBottom: '4px',
  marginRight: "10px"
};

const radioCardStyle = {
  // Makes the option take up equal width (1/3 of the container)
  flex: 1,
  textAlign: 'center',
  height: 'auto', // Allow content to determine height
  padding: '10px 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '2px', // Smooth corners
};
const cfURLCleaner = (url) => {

  let burl = url.replace("489fc937963b0464dbb90ad472e9c8a5.r2.cloudflarestorage.com/instantdm-images", "sdc.instantdm.in")

  // console.log("burl-=========0", burl)

  return burl
}

const VideoPreview = memo(({ url }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {!loaded && (
        <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)"
        }}>
          <Spin />
        </div>
      )}
      <video
        src={cfURLCleaner(url)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: loaded ? "block" : "none"
        }}
        onLoadedData={() => setLoaded(true)}
        preload="metadata"
      />
    </div>
  );
});

// Memoized image preview
const ImagePreview = memo(({ url, alt }) => (
  <img
    src={cfURLCleaner(url)}
    alt={alt || "preview"}
    style={{ width: "100%", height: "100%", objectFit: "cover" }}
  />
));

// Optimized drag item component
const DragItemHorizontal = memo(({ file, index, moveItem, removeItem, openAltEditor }) => {
  const ref = React.useRef(null);

  const [, drop] = useDrop({
    accept: "mediaItem",
    hover(item) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: "mediaItem",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const isVideo = file.type?.startsWith("video/");

  return (
    <div
      ref={ref}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        width: 120,
        height: 120,
        position: "relative",
        borderRadius: 8,
        overflow: "hidden",
        border: "2px solid #d9d9d9",
        flexShrink: 0,
      }}
    >
      {isVideo ? (
        <VideoPreview url={file.url} />
      ) : (
        <ImagePreview url={file.url} alt={file.alt} />
      )}

      {/* Alt Text Icon */}
      {!isVideo && <div
        onClick={() => openAltEditor(index)}
        style={{
          position: "absolute",
          bottom: 4,
          left: 4,
          color: "#fff",
          background: "rgba(0,0,0,0.7)",
          borderRadius: 4,
          padding: "2px 6px",
          fontSize: 10,
          cursor: "pointer",
          fontWeight: "bold",
        }}
      >
        ALT
      </div>}
      {isVideo && (
        <PlayCircleOutlined
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            fontSize: 32,
            pointerEvents: "none",
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
          }}
        />
      )}

      {/* Delete */}
      <DeleteOutlined
        onClick={() => removeItem(index)}
        style={{
          position: "absolute",
          top: 4,
          right: 4,
          color: "#fff",
          background: "rgba(0,0,0,0.5)",
          borderRadius: "50%",
          padding: 4,
          cursor: "pointer",
        }}
      />
    </div>
  );
});

const extractThumbnail = (videoFile) => {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.src = URL.createObjectURL(videoFile);
    video.crossOrigin = "anonymous";
    video.muted = true;

    video.addEventListener("loadeddata", () => {
      video.currentTime = 0.1;
    });

    video.addEventListener("seeked", () => {
      const canvas = document.createElement("canvas");
      canvas.width = 640;
      canvas.height = 640;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        resolve(url);
      }, "image/jpeg");
    });
  });
};


// --- DUMMY DATA GENERATOR ---
const generateDummyData = () => {

  return {
    [""]: [
      {
        id: "1",
        postType: "image",
        scheduleTime: dayjs().hour(10).minute(30), // stored as dayjs for simplicity in this demo
        caption: "Morning vibes! ☀️",
        media: [{ url: "https://picsum.photos/id/10/800/800", type: "image/jpeg", name: "morning.jpg" }],
        thumbnail: null
      },
      {
        id: "2",
        postType: "reel",
        scheduleTime: dayjs().hour(14).minute(0),
        caption: "Check out this transition! 🎥",
        media: [{ url: "https://www.w3schools.com/html/mov_bbb.mp4", type: "video/mp4", name: "video.mp4" }],
        thumbnail: "https://picsum.photos/id/20/300/500" // Mock thumbnail
      }
    ],
    [""]: [
      {
        id: "3",
        postType: "carousel",
        scheduleTime: dayjs().hour(18).minute(15),
        caption: "Photo dump from the weekend 📸",
        media: [
          { url: "https://picsum.photos/id/15/800/800", type: "image/jpeg", name: "1.jpg" },
          { url: "https://picsum.photos/id/16/800/800", type: "image/jpeg", name: "2.jpg" }
        ],
        thumbnail: null
      }
    ]
  };
};


const transformSchedules = (apiData) => {
  const grouped = {};

  apiData.forEach((post) => {
    // 1. Parse the Local Time to a DayJS Object
    const dateObj = dayjs(post.schedule_time_local);
    const dateKey = dateObj.format("YYYY-MM-DD");

    // 2. Determine Post Type (Fallback logic if API returns null)
    let pType = post.post_type;
    if (!pType) {
      // Simple inference logic
      if (post.media && post.media.length > 1) pType = 'carousel';
      else if (post.media?.[0]?.type?.includes('video')) pType = 'reel';
      else pType = 'image';
    }

    // 3. Format Media Items for the UI
    const formattedMedia = (post.media || []).map((m, idx) => ({
      uid: `${post.schedule_id}-${idx}`, // Unique ID for React keys
      url: m.url,
      type: m.type, // e.g. "image/png"
      alt: m.alt || "",
      name: `Media ${idx + 1}`, // Fallback name
      status: 'done' // Mark as done so progress bar doesn't show
    }));

    // 4. Create the Post Object
    const cleanPost = {
      id: post.schedule_id || post.id, // Handle ID variations
      postType: pType,
      scheduleTime: dateObj, // IMPT: Must be DayJS object for Form
      caption: post.caption || "",
      media: formattedMedia,
      thumbnail: post.thumbnail || null,
      media_id: post.media_id,
      published: post.published,
      steps: post.steps
    };

    // 5. Add to the Group
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(cleanPost);
  });

  return grouped;
};

export default function InstagramPostUploader() {
  // State
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [scheduledPosts, setScheduledPosts] = useState({}); // Start empty, load in useEffect
  const [modalOpen, setModalOpen] = useState(false);

  // Form & Editing State
  const [mediaFiles, setMediaFiles] = useState([]);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailAuto, setThumbnailAuto] = useState(null);
  const [editingPostId, setEditingPostId] = useState(null); // NULL = New Post, ID = Editing
  const [postType, setPostType] = useState("carousel");
  const [altModal, setAltModal] = useState({ open: false, index: null, text: "" });
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const [form] = Form.useForm();
  const scheduleTimeValue = Form.useWatch('scheduleTime', form);
  const [isSaving, setIsSaving] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [showDMAutomation, setShowDMAutomation] = useState(false);


  const [isThumbnailUploading, setIsThumbnailUploading] = useState(false);
  const [frameSelectorOpen, setFrameSelectorOpen] = useState(false);
  // Ref for the video in the frame selector
  const frameVideoRef = React.useRef(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);

  const [openFormSubmit, setOpenFormSubmit] = useState(false)

  // Saved Captions State
  const [savedCaptions, setSavedCaptions] = useState([]);
  const [isCaptionsModalOpen, setIsCaptionsModalOpen] = useState(false);
  const [isSaveCaptionModalOpen, setIsSaveCaptionModalOpen] = useState(false);
  const [newCaptionName, setNewCaptionName] = useState("");
  const [captionSearch, setCaptionSearch] = useState("");
  const [editingCaption, setEditingCaption] = useState(null); // { id, name, text }
  const [isEditCaptionModalOpen, setIsEditCaptionModalOpen] = useState(false);


  let user_detail = JSON.parse(localStorage.getItem("userData"))

  // --- LOAD DUMMY DATA ON MOUNT ---
  useEffect(() => {

    callAPI({
      url: "/schedule", // Change endpoints as needed
      method: "GET",
      loadingState: setIsLoading, // Binds to the new state
      dataState: (response) => {

        if (response?.code != 200) {
          message.error("Unable to fetch")
          return
        }
        setScheduledPosts(transformSchedules(response?.data || []))

      }
    });

  }, []);

  const [isPastPost, setIsPastPost] = useState(false)

  const [editingPost, setEditingPost] = useState({})

  const [showVideoPopup, setShowVideoPopup] = useState(false)
  const [videoSrc, setVideoSrc] = useState(
    "https://www.youtube-nocookie.com/embed/_28lJKsayLY?si=JrzIs4d9BZzddKYd"
  );


  const handleVideoClose = () => {
    setShowVideoPopup(false);
    setVideoSrc("");      // stops video
    setTimeout(() => {
      setVideoSrc("https://www.youtube-nocookie.com/embed/_28lJKsayLY?si=JrzIs4d9BZzddKYd");
    }, 200);              // restore once modal is fully closed
  };
  // --- CALENDAR RENDER LOGIC ---
  const dateCellRender = (value) => {
    const dateKey = value.format("YYYY-MM-DD");
    const posts = scheduledPosts[dateKey] || [];
    // console.log("===========posts", dateKey, posts)
    if (posts?.length < 1) {
      return
    }
    return (
      <div
        className="data-cell"
        style={{
          cursor: "pointer",
          borderRadius: 8,
          background: "#7e99a334",
          padding: 6,
          transition: "0.2s ease",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflow: "hidden", // ✅ hides scrollbar and content overflow
          scrollbarWidth: "none", // ✅ hides scrollbar for Firefox
          msOverflowStyle: "none", // ✅ hides scrollbar for IE/Edge
        }}>
        <ul clas sName="events" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {posts.map((post) => (
            <li key={post.id} onClick={(e) => { e.stopPropagation(); openEditModal(post, setIsPastPost); }}>
              <div style={{
                background: "#fff",
                border: "1px solid #f0f0f0",
                padding: "4px",
                borderRadius: "6px",
                marginBottom: "4px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                {/* Icon based on type */}
                <Avatar
                  shape="square"
                  size="small"
                  src={post.postType === 'reel' ? post.thumbnail : post.media[0]?.url}
                  icon={post.media_id ? <CheckCircleFilled style={{ color: "green" }} /> : post.postType === 'reel' ? <VideoCameraOutlined /> : post.postType === 'carousel' ? <SplitCellsOutlined /> : <FileImageOutlined />}
                  style={{ backgroundColor: post.media_id ? '#e1fdcfff' : '#fde3cf', color: '#f56a00' }}
                />

                <div style={{ overflow: "hidden", flex: 1 }}>
                  <div style={{ fontSize: "10px", fontWeight: "bold", color: "#888" }}>
                    {dayjs(post.scheduleTime).format("h:mm A")}
                  </div>
                  <div style={{ fontSize: "11px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {post.caption || <em>No Caption</em>}
                  </div>
                </div>

                {/* Type Badge */}
                <Tag style={{ marginRight: 0, fontSize: '9px' }} color={post.postType === 'reel' ? 'purple' : post.postType === 'carousel' ? 'green' : 'blue'}>
                  {post.postType === 'reel' ? 'REEL' : post.postType === 'carousel' ? 'CAR' : 'IMG'}
                </Tag>
              </div>
            </li>
          ))}
          {/* <Button color="primary" style={{margin:0}} className="data-cell-create-btn">+ New Post</Button> */}


        </ul>
      </div>
    );
  };

  // --- ACTIONS ---

  const duplicatePost = () => {
    // 1. Get the current form values (which should be the past post's data)
    const values = form.getFieldsValue();

    // 2. Clear the editing ID to switch to 'Create' mode
    setEditingPostId(null);
    setIsPastPost(false)
    setEditingPost({})


    // 3. Set a new, default future schedule time
    // Example: Set to tomorrow at 12:00 PM
    const tomorrowAtNoon = dayjs().add(1, 'day').hour(12).minute(0).second(0);

    // 4. Update the form fields for the new schedule
    const {
      schedule_id,
      schedule_time_ist,
      schedule_time_local,
      schedule_time_utc,
      media_id,
      id,
      steps,
      published,
      ...remainingValues // Collect all other keys into a new object
    } = values;
    form.setFieldsValue({
      ...values,
      scheduleTime: tomorrowAtNoon,
    });
    // The modal remains open, but now the footer shows the "Schedule" button 
    // and the user must pick a valid future time.
    message.info('Post duplicated. Please select a new future schedule time.');
  };

  // 1. Open New Schedule (Reset everything)
  const openNewSchedule = useCallback(() => {
    form.resetFields();
    setMediaFiles([]);
    setThumbnail(null);
    setIsPastPost(false);
    setThumbnailAuto(null);
    setEditingPostId(null); // Ensure we are in Create mode
    setPostType("carousel"); // Default
    setIsThumbnailUploading(false); // Reset upload state
    setEditingPost({})

    const oneHourFromNow = dayjs().add(1, 'hour');

    // 2. Auto-set date to selected calendar date, but use the calculated time
    const newScheduleTime = selectedDate
      .hour(oneHourFromNow.hour()) // Set the hour to +1 hour from now
      .minute(oneHourFromNow.minute()) // Set the minute to the current minute
      .second(0); // Optional: Clear seconds for clean time setting

    form.setFieldValue("scheduleTime", newScheduleTime);
    // Auto-set date to selected calendar date

    setModalOpen(true);

  }, [form, selectedDate]);

  // 2. Open Edit Modal (Load data)
  const openEditModal = (post) => {
    setEditingPostId(post.id);
    setPostType(post.postType);
    setThumbnail(post.thumbnail);
    setThumbnailAuto(null); // Reset auto, user has a saved one or null

    console.log("=============POST", post)
    setEditingPost(post)
    // Populate Media
    // Important: We use the existing objects. In a real API, you'd map the URLs to objects.
    setMediaFiles(post.media);
    const isPastPost = post.scheduleTime.isBefore(dayjs());
    setIsPastPost(isPastPost);

    // Populate Form
    form.setFieldsValue({
      postType: post.postType,
      scheduleTime: post.scheduleTime, // Ensure this is a DayJS object
      caption: post.caption
    });

    setModalOpen(true);
  };

  // // 3. Save (Handle both Create and Update)
  // const saveSchedule = useCallback((values) => {
  //   const dateKey = values.scheduleTime.format("YYYY-MM-DD");

  //   const newPostData = {
  //     id: editingPostId || uuidv4(), // Use existing ID or new one
  //     ...values,
  //     media: mediaFiles,
  //     thumbnail
  //   };

  //   console.log("======newPostData========", newPostData,)

  //   setScheduledPosts((prev) => {
  //     const newPosts = { ...prev };

  //     // If editing, we might need to remove the old entry first (in case date changed)
  //     if (editingPostId) {
  //       // Find where it was before (inefficient loop, but fine for dummy data)
  //       Object.keys(newPosts).forEach(key => {
  //         newPosts[key] = newPosts[key].filter(p => p.id !== editingPostId);
  //         if (newPosts[key].length === 0) delete newPosts[key];
  //       });
  //     }

  //     // Add to the correct date bucket
  //     if (!newPosts[dateKey]) newPosts[dateKey] = [];
  //     newPosts[dateKey].push(newPostData);

  //     // Sort by time
  //     newPosts[dateKey].sort((a, b) => a.scheduleTime - b.scheduleTime);

  //     return newPosts;
  //   });

  //   setModalOpen(false);
  //   message.success(editingPostId ? "Schedule updated!" : "Post scheduled!");
  //   cleanupUrls(mediaFiles); // Clean blobs if any
  // }, [mediaFiles, thumbnail, editingPostId]);


  const saveSchedule = useCallback((values) => {
    // 1. Safety Check: Ensure no uploads are pending
    if (mediaFiles.some(f => f.status === 'uploading')) {
      message.warning("Please wait for files to finish uploading");
      return;
    }

    if (isThumbnailUploading) {
      message.warning("Please wait for thumbnail to finish uploading");
      return;
    }

    // 2. Get User Timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const scheduleTimeLocal = values.scheduleTime;

    const scheduleTimeUTC = scheduleTimeLocal.utc().format("YYYY-MM-DD HH:mm:ss");
    const scheduleTimeIST = scheduleTimeLocal.tz("Asia/Kolkata").format("YYYY-MM-DD HH:mm:ss");
    // 3. Prepare the Payload
    // We convert dayjs object to string and clean up the media array for the backend
    const payload = {
      id: editingPostId || null, // Send ID if editing, null if new
      caption: values.caption,
      post_type: values.postType, // Ensure casing matches your backend expectation
      schedule_time_local: scheduleTimeLocal.format("YYYY-MM-DD HH:mm:ss"), // What the user saw
      schedule_time_utc: scheduleTimeUTC, // Standard for DB
      schedule_time_ist: scheduleTimeIST, // Specific request
      user_timezone: userTimezone,
      thumbnail: thumbnail,
      media: mediaFiles.map(m => ({
        url: m.url,
        type: m.type || (m.file?.type.startsWith('video') ? 'VIDEO' : 'IMAGE'),
        alt: m.alt || ""
      }))
    };

    // 4. Call the API
    callAPI({
      url: "/schedule", // Change endpoints as needed
      method: "POST",
      body: payload,
      loadingState: setIsSaving, // Binds to the new state
      dataState: (response) => {

        if (response?.code != 200) {
          message.error("Can't publish the post")
          return
        }
        // --- SUCCESS CALLBACK ---

        // Update Local Calendar State
        // (We replicate the logic you had, but now it only runs on API success)
        const dateKey = values.scheduleTime.format("YYYY-MM-DD");

        // If the API returns the created object, use that. Otherwise use our local payload + ID.
        const confirmedPostData = {
          ...payload,
          id: response.create_id || payload.id,
          // Convert string back to Dayjs for the Calendar UI to read correctly
          scheduleTime: values.scheduleTime,
          postType: payload?.post_type
        };

        setScheduledPosts((prev) => {
          const newPosts = { ...prev };

          // Remove old entry if editing
          if (editingPostId) {
            Object.keys(newPosts).forEach(key => {
              newPosts[key] = newPosts[key].filter(p => p.id !== editingPostId);
              if (newPosts[key].length === 0) delete newPosts[key];
            });
          }

          // Add new entry
          if (!newPosts[dateKey]) newPosts[dateKey] = [];
          newPosts[dateKey].push(confirmedPostData);
          newPosts[dateKey].sort((a, b) => a.scheduleTime - b.scheduleTime);

          return newPosts;
        });

        // Cleanup UI
        setModalOpen(false);
        message.success(editingPostId ? "Schedule updated!" : "Post scheduled!");
        if (!editingPostId) {
          setShowDMAutomation(confirmedPostData?.id)
        }
        cleanupUrls(mediaFiles);
        setMediaFiles([]);
        setThumbnail(null);
      }
    });

  }, [mediaFiles, thumbnail, editingPostId]);


  // 4. Delete Post
  const deletePost = () => {
    if (!editingPostId) return;

    // console.log("====",editingPostId)
    // return 



    callAPI({
      url: "/schedule", // Change endpoints as needed
      method: "DELETE",
      body: { "schedule_id": editingPostId },
      loadingState: setIsSaving, // Binds to the new state
      dataState: (response) => {

        if (response?.code != 200) {
          message.error("Can't delete the post")
          return
        }
        // --- SUCCESS CALLBACK ---

        setScheduledPosts(prev => {
          const newPosts = { ...prev };
          Object.keys(newPosts).forEach(key => {
            newPosts[key] = newPosts[key].filter(p => p.id !== editingPostId);
          });
          return newPosts;
        });

        setModalOpen(false);
        message.success("Post deleted successfully");
        setThumbnail(null);
      }
    });


  };

  // --- HELPERS ---

  const isScheduleTimeValid = () => {
    if (!scheduleTimeValue) return false;
    // Allow editing past posts without validation blocking? 
    // Or strictly future? Let's allow >= now.
    return scheduleTimeValue.isAfter(dayjs().subtract(5, 'minutes'));
  };

  const cleanupUrls = (files) => {
    files.forEach((file) => {
      if (file.url?.startsWith("blob:")) URL.revokeObjectURL(file.url);
    });
  };

  const handleCancelModal = () => {
    if ((mediaFiles.length > 0 || form.getFieldValue('caption') || editingPostId) && !isPastPost) {
      setCancelConfirmOpen(true);
    } else {
      setModalOpen(false);
    }
  };

  const confirmCancel = () => {
    setModalOpen(false);
    setCancelConfirmOpen(false);
    form.resetFields();
    setMediaFiles([]);
    setEditingPostId(null);
  };

  // --- SAVED CAPTIONS LOGIC ---

  const fetchSavedCaptions = async () => {
    if (!user_detail?.user_id) return;
    try {
      const q = query(collection(fireDB, "saved_captions"), where("user_id", "==", user_detail.user_id));
      const querySnapshot = await getDocs(q);
      const captions = [];
      querySnapshot.forEach((doc) => {
        captions.push({ id: doc.id, ...doc.data() });
      });
      // Sort by recent?
      captions.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
      setSavedCaptions(captions);
    } catch (err) {
      console.error("Error fetching captions:", err);
      message.error("Failed to load saved captions");
    }
  };

  const openCaptionsList = () => {
    fetchSavedCaptions();
    setIsCaptionsModalOpen(true);
  };

  const handleSaveCurrentCaption = async () => {
    const text = form.getFieldValue("caption");
    if (!text || !text.trim()) {
      message.warning("Please enter a caption first");
      return;
    }
    setNewCaptionName("");
    setIsSaveCaptionModalOpen(true);
  };

  const confirmSaveCaption = async () => {
    if (!newCaptionName.trim()) {
      message.error("Please provide a name for this caption");
      return;
    }
    const text = form.getFieldValue("caption");
    if (!user_detail?.user_id) {
      message.error("User not found");
      return;
    }
    try {
      await addDoc(collection(fireDB, "saved_captions"), {
        user_id: user_detail.user_id,
        name: newCaptionName,
        text: text,
        createdAt: serverTimestamp()
      });
      message.success("Caption saved!");
      setIsSaveCaptionModalOpen(false);
      // Refresh list if open? It's not open but we can update state if we want
    } catch (err) {
      console.error(err);
      message.error("Failed to save caption");
    }
  };

  const handleUseCaption = (text) => {
    const currentCaption = form.getFieldValue("caption") || "";
    const newCaption = currentCaption ? `${currentCaption}\n${text}` : text;
    form.setFieldValue("caption", newCaption);
    setIsCaptionsModalOpen(false);
    message.success("Caption appended!");
  };

  const handleDeleteCaption = async (id) => {
    try {
      await deleteDoc(doc(fireDB, "saved_captions", id));
      message.success("Caption deleted");
      setSavedCaptions(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error(err);
      message.error("Failed to delete caption");
    }
  };

  const startEditCaption = (cap) => {
    setEditingCaption(cap);
    setIsEditCaptionModalOpen(true);
  };

  const saveEditedCaption = async () => {
    if (!editingCaption || !editingCaption.name?.trim() || !editingCaption.text?.trim()) {
      message.error("Name and Text are required");
      return;
    }

    try {
      await updateDoc(doc(fireDB, "saved_captions", editingCaption.id), {
        name: editingCaption.name,
        text: editingCaption.text,
        updatedAt: serverTimestamp()
      });
      message.success("Caption updated");
      setIsEditCaptionModalOpen(false);
      // Update local state
      setSavedCaptions(prev => prev.map(c => c.id === editingCaption.id ? { ...c, name: editingCaption.name, text: editingCaption.text } : c));
      setEditingCaption(null);
    } catch (err) {
      console.error(err);
      message.error("Failed to update caption");
    }
  };

  const filteredCaptions = savedCaptions.filter(c =>
  (c.name?.toLowerCase().includes(captionSearch.toLowerCase()) ||
    c.text?.toLowerCase().includes(captionSearch.toLowerCase()))
  );


  const handlePostTypeChange = (value) => {
    // let value = e.target.value
    console.log("Value change================", value)
    setPostType(value);
    if (value === 'image' || value === 'reel') {
      if (mediaFiles.length > 1) {
        setMediaFiles(mediaFiles.slice(0, 1));
      }
    }
  };


  // Helper to upload a blob/file and set it as thumbnail
  const uploadAndSetThumbnail = async (fileOrBlob) => {
    setIsThumbnailUploading(true);
    // Create preview immediate for UI feedback
    const previewUrl = URL.createObjectURL(fileOrBlob);
    setThumbnail(previewUrl);

    try {
      const uploadedUrl = await UploadFileToCF(
        null,
        fileOrBlob,
        "scheduler_thumb",
        uuidv4(),
        "IMAGE",
        null
      );

      if (uploadedUrl) {
        setThumbnail(uploadedUrl);
        // If we generated an auto thumbnail (e.g. from 0.1s), keep it as the auto option too
        // but make sure it's the REMOTE url, not the blob
        if (fileOrBlob === thumbnailAuto) { // This comparison won't work for blobs, logic needs adjustment
          // Simplification: We don't strict check here.
        }
      } else {
        message.error("Thumbnail upload failed");
        // Revert to null or keep blob? Revert is safer to force retry
        setThumbnail(null);
      }
    } catch (e) {
      console.error(e);
      message.error("Thumbnail upload failed");
      setThumbnail(null);
    } finally {
      setIsThumbnailUploading(false);
      // Clean up local preview
      URL.revokeObjectURL(previewUrl);
    }
  };

  const handleFileUpload = async (file, isThumbnail = false) => {
    const tempId = uuidv4();
    const localUrl = URL.createObjectURL(file);
    const fileType = file.type.startsWith("video") ? "VIDEO" : "IMAGE";

    // A. If it's a main media item
    if (!isThumbnail) {
      const newFileObj = {
        uid: tempId,
        file: file,
        url: localUrl, // Show local preview immediately
        name: file.name,
        type: file.type,
        alt: "",
        status: 'uploading', // Start in uploading state
        uploadProgress: 0
      };

      setMediaFiles(prev => [...prev, newFileObj]);

      // Perform Upload
      const uploadedUrl = await UploadFileToCF(
        message,
        file,
        "scheduler_upload", // product_id (Static or dynamic)
        tempId,             // media_id
        fileType,
        (percent) => {
          // Update Progress
          setMediaFiles(prev => prev.map(item =>
            item.uid === tempId ? { ...item, uploadProgress: percent } : item
          ));
        }
      );

      if (uploadedUrl) {
        // Success: Swap Blob URL with Cloud URL
        setMediaFiles(prev => prev.map(item => {
          if (item.uid === tempId) {
            URL.revokeObjectURL(item.url); // Free memory
            return { ...item, url: uploadedUrl, status: 'done' };
          }
          return item;
        }));
      } else {
        // Error handling
        message.error(`${file.name} upload failed.`);
        setMediaFiles(prev => prev.filter(item => item.uid !== tempId));
      }
    }
    // B. If it's a Thumbnail (CUSTOM UPLOAD)
    else {
      // Use the new helper
      await uploadAndSetThumbnail(file);
      message.success("Custom thumbnail uploaded");
    }
  };

  // Re-use your beforeUpload logic here...
  const beforeUpload = useCallback((file) => {
    const valid = ["image/jpeg", "image/png", "image/webp", "video/mp4", "video/quicktime"];
    if (!valid.includes(file.type)) {
      message.error("Unsupported file type");
      return Upload.LIST_IGNORE;
    }
    handleFileUpload(file, false);
    const objectUrl = URL.createObjectURL(file);
    // setMediaFiles((prev) => [
    //   ...prev,
    //   { file, url: objectUrl, name: file.name, type: file.type, alt: "" },
    // ]);

    // Auto generate thumbnail logic... (Same as your original code)
    // Auto generate thumbnail logic... 
    if (postType === 'reel' && file.type.startsWith("video/")) {
      extractThumbnail(file).then((blobUrl) => {
        // extractThumbnail returns a blob URL string in the original code, 
        // but we need the actual BLOB to upload it.
        // Let's fetch the blob from the URL
        fetch(blobUrl).then(r => r.blob()).then(blob => {
          // We store the blob URL as "Auto" option so we can revert to it? 
          // Actually, best to just upload it immediately.
          setThumbnailAuto(blobUrl); // Keep the blob URL for reference/re-selection? 
          // Better: Upload it immediately and set THAT as auto

          setIsThumbnailUploading(true);
          UploadFileToCF(null, blob, "scheduler_thumb", uuidv4(), "IMAGE", null).then(remoteUrl => {
            if (remoteUrl) {
              setThumbnailAuto(remoteUrl);
              setThumbnail(remoteUrl);
            }
            setIsThumbnailUploading(false);
          });
        });
      });
    }

    return Upload.LIST_IGNORE;
  }, [postType]);

  const moveItem = useCallback((from, to) => {
    setMediaFiles((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      return updated;
    });
  }, []);

  const removeItem = useCallback((index) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const openAltEditor = useCallback((index) => {
    setAltModal({ open: true, index, text: mediaFiles[index]?.alt || "" });
  }, [mediaFiles]);

  const saveAltText = () => {
    setMediaFiles(prev => {
      const up = [...prev];
      up[altModal.index].alt = altModal.text;
      return up;
    });
    setAltModal({ open: false, index: null, text: "" });
  };


  const isUploading = mediaFiles.some(f => f.status === 'uploading');

  const WeeklyView = ({ initialDate, dateCellRender, scheduledPosts }) => {
    // Use internal state to manage the date currently displayed in the grid
    const [currentWeekStart, setCurrentWeekStart] = React.useState(
      initialDate.startOf('week')
    );

    // --- NAVIGATION HANDLERS ---
    const navigateWeek = (numWeeks) => {
      setCurrentWeekStart(prev => prev.add(numWeeks, 'week'));
    };

    const goToToday = () => {
      setCurrentWeekStart(dayjs().startOf('week'));
    };

    // --- WEEK GENERATION LOGIC ---
    const weekDays = [];
    let start = currentWeekStart;

    // Generate 7 days for the visible week
    for (let i = 0; i < 14; i++) {
      const day = start.add(i, 'day').clone();
      weekDays.push(day);
    }

    // --- RENDER LOGIC ---

    // Title showing the range of the current week
    const weekTitle = `${currentWeekStart.format('MMM D')} - ${currentWeekStart.add(6, 'day').format('MMM D, YYYY')}`;

    return (
      <div style={{ padding: 16 }}>

        {/* Navigation Header */}
        <Flex wrap justify="space-between" align="center" style={{ marginBottom: 16 }}>
          <h3>{weekTitle}</h3>
          <Flex wrap gap="small">
            <Button onClick={() => navigateWeek(-2)}>Prev</Button>
            <Button onClick={goToToday}>Today</Button>
            <Button onClick={() => navigateWeek(2)}>Next</Button>
          </Flex>
        </Flex>

        <div
          style={{
            display: 'flex',           // Use Flexbox for responsive wrapping
            flexWrap: 'wrap',          // Crucial: Allows items to move to a new line
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            overflow: 'hidden',
            // Note: Removed gridTemplateColumns
          }}
        >
          {weekDays.map((day, index) => {
            // 1. Check if the day is before the current day
            const isPastDay = day.isBefore(dayjs(), 'day');

            return (
              <div
                key={day.toISOString()}
                style={{
                  minWidth: '200px',
                  flex: '1 0 150px',
                  minHeight: 200,
                  padding: 8,
                  borderRight: '1px solid #f0f0f0',
                  borderBottom: '1px solid #f0f0f0',

                  // 2. Conditional Styling: Fade if it's a past day
                  opacity: isPastDay ? 0.6 : 1, // Fades the cell
                  transition: 'opacity 0.3s ease', // Smooth transition (optional)

                  // Highlight for Today
                  background: day.isSame(dayjs(), 'day')
                    ? '#f0f0ff'
                    : isPastDay ? '#fafafa' : 'transparent', // Light background for past days
                }}
              >
                {/* Day Label */}
                <div style={{ textAlign: 'center', marginBottom: 8, fontSize: "12px", fontWeight: '400' }}>
                  {day.format('ddd, MMM D')}
                </div>

                {/* Render the cell content using your existing function */}
                {dateCellRender(day)}
              </div>)
          })
          }
        </div>


      </div>
    );
  };


  return (
    <DndProvider backend={HTML5Backend}>
      <div className="page-layout">
        {/* {contextHolder} */}
        <Sider />

        <div className="page-content">

          <Content className="later-padding">
            <Flex wrap justify="space-between" align="center" style={{ marginBottom: 16, flexDirection: 'column', alignItems: 'flex-start' }}>

              {/* Title and Tag (Header Row) */}
              <Flex justify="space-between" align="center" style={{ width: '100%' }} wrap>
                <h2>InstantDM's Later - Schedule Content with AutoDM</h2>
                <Flex gap={"10px"} wrap>
                  <Button color="purple" variant="filled" icon={<VideoCameraOutlined />} onClick={() => setShowVideoPopup(true)}>
                    How to use? Watch Video!
                  </Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={AccessOrNot("post_schedules") ? openNewSchedule : () => {
                    message.error("Please upgrade your plan to access this feature");
                  }}>
                    New Schedule {<CrownReturn premiumUser={AccessOrNot("post_schedules")} />}
                  </Button>
                  <Button color="default" variant="outlined" icon={<CommentOutlined />} onClick={() => setOpenFormSubmit(true)}>
                    Feedback
                  </Button>
                </Flex>
              </Flex>

              {/* Description Added Here */}
              <p style={{ color: '#888', marginTop: 4, marginBottom: 0, fontSize: '14px' }}>
                Plan and manage all your upcoming instagram posts. Click on a new schedule to upload post.
              </p>

            </Flex>

            <div style={{ background: '#fff', padding: 20, borderRadius: 10 }}>

              <WeeklyView
                initialDate={selectedDate}
                dateCellRender={dateCellRender} // Pass your existing function
                scheduledPosts={scheduledPosts} // Pass the data
              />

            </div>
          </Content>

          <Modal
            title={editingPostId ? "Edit Scheduled Post" : "Schedule Post"}
            open={modalOpen}
            onCancel={handleCancelModal}
            closable={false}
            width={720}
            footer={null} // Custom footer in form
          >
            {
              editingPost?.media_id && <Alert
                message="Post Published"
                type="success"
                showIcon
                style={{ marginBottom: "10px" }}
              />
            }
            {(!editingPost?.media_id && editingPostId && !isPastPost) && <Alert style={{ marginBottom: "10px" }} message="Comment Automation"
              description="Setup/Edit Comment-to-DM Automation for this post. Your automation will be applied instantly at the time of publish with no delay..."
              action={
                <Button
                  color="default"
                  variant="outlined"
                  href={`manage-post/${editingPostId}?post_type=posts&flow_editor=false&platform_type=insta&scheduled_post=true`}
                  icon={<SettingOutlined />}
                >
                  Go to Automation Setup
                </Button>
              }
            />}
            <Form form={form} layout="vertical" onFinish={saveSchedule}>
              {/* <Form.Item name="postType" label="Post Type" initialValue="carousel">
              <Select onChange={handlePostTypeChange}>
                <Select.Option value="image">Image</Select.Option>
                <Select.Option value="carousel">Carousel</Select.Option>
                <Select.Option value="reel">Reel</Select.Option>
              </Select>
            </Form.Item> */}


              <Form.Item
                name="postType"
                label="Post Type"
                initialValue="carousel"
              // Note: We don't need the external onChange={handlePostTypeChange} 
              // if you use the Radio.Group's onChange, which is automatically handled by Form.Item.
              >

                <Segmented
                  size={'large'}
                  shape="default"
                  onChange={handlePostTypeChange}
                  options={[
                    { value: 'image', icon: <FileImageOutlined />, label: "Image" },
                    { value: 'carousel', icon: <SplitCellsOutlined />, label: "Carousel" },
                    { value: 'reel', icon: <VideoCameraOutlined />, label: "Reel" },


                  ]}
                />

              </Form.Item>

              <Form.Item name="scheduleTime" label="Schedule Time" rules={[{ required: true }]}>
                <DatePicker
                  showTime={{ minuteStep: 5, format: "HH:mm", use12Hours: true }}
                  // format="YYYY-MM-DD HH:mm"
                  format="YYYY-MM-DD hh:mm A"
                  style={{ width: "100%" }}
                  disabledDate={(current) => {

                    return current && current < dayjs().startOf("day")

                  }}

                  disabledTime={(date) => {

                    if (!date) return {}

                    const isToday = date.isSame(dayjs(), "day")

                    if (!isToday) return {}

                    return {

                      disabledHours: () =>

                        Array.from({ length: 24 }, (_, i) => i).filter(

                          (h) => h < dayjs().hour()

                        ),

                    }

                  }}

                />
              </Form.Item>

              <Form.Item label={<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>Caption</span>
                <Space size="small">
                  <Button size="small" type="link" icon={<BookOutlined />} onClick={openCaptionsList}>Saved Captions</Button>
                  <Button size="small" type="link" icon={<SaveOutlined />} onClick={handleSaveCurrentCaption}>Save Caption</Button>
                </Space>
              </div>} name="caption" >
                <TextArea rows={4} placeholder="Write your caption..." />
              </Form.Item>

              {/* Upload Area */}
              {((postType === 'image' && mediaFiles.length < 1) ||
                (postType === 'reel' && mediaFiles.length < 1) ||
                (postType === 'carousel')) && (
                  <Dragger
                    multiple={postType === 'carousel'}
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                  >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">Click or drag to upload</p>
                  </Dragger>
                )}

              {/* Thumbnail Section for Reels (Simplified) */}
              {/* {postType === "reel" && mediaFiles.length === 1 && (
               <div style={{ marginTop: 16 }}>
                   <p>Thumbnail Preview:</p>
                   <img src={thumbnail || thumbnailAuto} style={{ height: 100, border: '1px solid #ddd' }} alt="thumb"/>
               </div>
            )} */}

              {postType === "reel" && mediaFiles.length === 1 && (
                <>
                  <Divider orientation="left">Cover Photo</Divider>
                  <div
                    style={{
                      width: 90,  // small size
                      height: 160, // perfectly 9:16
                      border: "1px solid #ccc",
                      borderRadius: 8,
                      overflow: "hidden",
                      marginBottom: 12,
                      background: "#f0f0f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative"
                    }}
                  >
                    {isThumbnailUploading && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Spin size="small" />
                      </div>
                    )}
                    {thumbnail ? (
                      <img
                        src={cfURLCleaner(thumbnail)}
                        alt="thumbnail"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: 12 }}>No thumbnail</div>
                    )}
                  </div>
                  <Flex wrap gap="small">
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        // The logic is now handled in handleFileUpload called here
                        handleFileUpload(file, true);
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />}>Upload Custom</Button>
                    </Upload>


                    <Button
                      onClick={() => setFrameSelectorOpen(true)}
                    >
                      Select from Video
                    </Button>
                  </Flex>
                </>
              )}

              {/* Draggable Items */}
              <div style={{ display: "flex", gap: 12, marginTop: 16, overflowX: "auto", padding: "8px 0", flexWrap: "wrap" }}>
                {mediaFiles.map((m, i) => (
                  <DragItemHorizontal
                    key={i}
                    file={m}
                    index={i}
                    moveItem={moveItem}
                    removeItem={removeItem}
                    openAltEditor={openAltEditor}
                  />
                ))}
              </div>

              {/* Footer Actions */}
              <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 10 }}>
                  {/* DELETE BUTTON - Only show if editing */}
                  {(editingPostId && !isPastPost) && (
                    <Popconfirm title="Delete this post?" onConfirm={deletePost} okText="Yes, Delete" okButtonProps={{ danger: true }}>
                      <Button danger icon={<DeleteOutlined />}>Delete Post</Button>
                    </Popconfirm>
                  )}
                </div>



                <div style={{ display: "flex", gap: 10 }}>

                  <Button onClick={handleCancelModal}>Cancel</Button>
                  {editingPostId && isPastPost && (
                    <Button
                      type="primary"
                      onClick={duplicatePost} // You need to implement this function
                      icon={<CopyOutlined />}
                    >
                      Duplicate
                    </Button>
                  )}
                  {!isPastPost && <Button loading={isUploading || isSaving} type="primary" htmlType="submit" disabled={mediaFiles.length === 0}>
                    {editingPostId ? "Update Schedule" : "Schedule"}
                  </Button>}
                </div>
              </div>
            </Form>
          </Modal>

          {/* Alt Text Modal (Same as before) */}
          <Modal
            title="Edit Alt Text"
            open={altModal.open}
            onOk={saveAltText}
            onCancel={() => setAltModal({ open: false, index: null, text: "" })}
          >
            <TextArea value={altModal.text} onChange={(e) => setAltModal({ ...altModal, text: e.target.value })} />
          </Modal>

          {/* Discard Confirmation */}
          <Modal
            title="Discard Changes?"
            open={cancelConfirmOpen}
            onOk={confirmCancel}
            onCancel={() => setCancelConfirmOpen(false)}
            okText="Discard"
            okButtonProps={{ danger: true }}
          >
            <p>You have unsaved changes. Are you sure you want to close?</p>
          </Modal>

          <Modal
            // Style the Modal itself for better visual hierarchy
            title={
              <Space>
                <SendOutlined style={{ color: '#1890ff' }} />
                <Title level={4} style={{ margin: 0, color: '#333' }}>
                  Scheduled, Next Automate Comments
                </Title>
              </Space>
            }
            open={showDMAutomation}
            onCancel={() => setShowDMAutomation(false)}

            // Custom footer for better button styling and placement
            footer={[
              <Button
                key="discard"
                onClick={() => setShowDMAutomation(false)}
              >
                Close
              </Button>,
              <Button
                key="submit"
                type="primary"
                href={`manage-post/${showDMAutomation}?post_type=posts&flow_editor=false&platform_type=insta&scheduled_post=true`}
                icon={<SettingOutlined />}
              >
                Setup Automation
              </Button>
            ]}
            // Add a style to the body for internal padding and background
            // bodyStyle={modalStyle}
            centered
            width={450}
          >
            <Result
              status="success"
              title="The post is Scheduled!"
              extra={[]}
            />
            <Space direction="vertical" style={{ width: '100%', marginTop: 20 }}>
              <Card size="small" style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
                <Text strong>Next Step: </Text>
                <Text>Automate Comment-to-DM for this post. Your automation will be applied instantly at the time of publish with no delay...</Text>
              </Card>
            </Space>
          </Modal>

          <Modal open={openFormSubmit} onCancel={() => setOpenFormSubmit(false)} footer={[]}>
            <iframe src={`https://app.youform.com/forms/opb1nhe2?email=${user_detail?.emailid}`} loading="lazy" width="100%" height="700" frameborder="0" marginheight="0" marginwidth="0"></iframe>
          </Modal>

          <Modal destroyOnClose width={"90%"} style={{ maxWidth: "800px" }} open={showVideoPopup} onCancel={handleVideoClose} footer={[]}>
            <Flex style={{ width: "100%", margin: "0 auto", padding: "auto" }}>
              <iframe
                style={{ margin: "0 auto", maxWidth: "600px", borderRadius: "10px" }}
                width="80%"
                height={"400px"} src={videoSrc} title="InstantDM - Later" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
            </Flex>
          </Modal>

          {/* Frame Selector Modal */}
          <Modal
            title="Select Cover Frame"
            open={frameSelectorOpen}
            onCancel={() => setFrameSelectorOpen(false)}
            onOk={() => {
              // Capture Logic
              if (frameVideoRef.current) {
                const video = frameVideoRef.current;

                // If the video hasn't loaded dimensions yet
                if (video.videoWidth === 0 || video.videoHeight === 0) {
                  message.error("Video not ready yet. Please wait.");
                  return;
                }

                try {
                  const canvas = document.createElement("canvas");
                  canvas.width = video.videoWidth;
                  canvas.height = video.videoHeight;
                  const ctx = canvas.getContext("2d");
                  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                  canvas.toBlob((blob) => {
                    if (blob) {
                      uploadAndSetThumbnail(blob);
                      message.success("Frame selected and uploading...");
                      setFrameSelectorOpen(false);
                    } else {
                      // This often happens if the canvas is tainted (CORS) or empty
                      message.error("Failed to capture frame. Possible security restriction.");
                    }
                  }, "image/jpeg", 0.95);
                } catch (e) {
                  console.error(e);
                  message.error("Error capturing frame: " + e.message);
                }
              }
            }}
            okText="Use this Frame"
            width={720}
            destroyOnClose // Ensure fresh mount every time
          >
            <div style={{ width: '100%', marginBottom: 20, background: '#000', borderRadius: 8, overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
              {mediaFiles.length > 0 && (
                <video
                  key={mediaFiles[0].uid || mediaFiles[0].url} // Force re-mount on change
                  ref={frameVideoRef}
                  src={cfURLCleaner(mediaFiles[0].url)} // Apply cleaner if it's a remote URL
                  style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}

                  // Metadata/Data Handlers
                  onLoadedMetadata={(e) => {
                    setVideoDuration(e.target.duration);
                  }}
                  onLoadedData={(e) => {
                    // Ensure we have a valid duration and update time
                    if (e.target.duration) setVideoDuration(e.target.duration);
                    e.target.currentTime = 0.1; // Seek to first frame to ensure it's not black
                  }}

                  crossOrigin="anonymous"
                  muted
                  playsInline
                  autoPlay={false}
                  preload="auto"
                  controls={false} // Hide default controls since we use slider
                />
              )}
            </div>

            <Flex gap="small" align="center">
              <div style={{ minWidth: 40, textAlign: 'right' }}>{currentVideoTime.toFixed(1)}s</div>
              <Slider
                style={{ flex: 1 }}
                min={0}
                max={videoDuration || 100} // Fallback to avoid crashes
                step={0.1}
                value={currentVideoTime}
                onChange={(val) => {
                  setCurrentVideoTime(val);
                  if (frameVideoRef.current) {
                    frameVideoRef.current.currentTime = val;
                  }
                }}
                tooltip={{ formatter: (val) => `${val.toFixed(1)}s` }}
              />
              <div style={{ minWidth: 40 }}>{videoDuration ? videoDuration.toFixed(1) : "0.0"}s</div>
            </Flex>

            <div style={{ textAlign: 'center', marginTop: 10, color: '#888', fontSize: 12 }}>
              Drag slider to pick the perfect cover frame
            </div>
          </Modal>
          {/* Saved Captions Modal */}
          <Modal
            title="Saved Captions"
            open={isCaptionsModalOpen}
            onCancel={() => setIsCaptionsModalOpen(false)}
            footer={null}
            width={600}
          >
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search captions..."
              value={captionSearch}
              onChange={(e) => setCaptionSearch(e.target.value)}
              style={{ marginBottom: 16 }}
            />

            <List
              dataSource={filteredCaptions}
              locale={{ emptyText: "No saved captions found" }}
              itemLayout="horizontal"
              pagination={{ pageSize: 5 }}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button size="small" type="link" onClick={() => handleUseCaption(item.text)}>Use</Button>,
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => startEditCaption(item)} />,
                    <Popconfirm title="Delete?" onConfirm={() => handleDeleteCaption(item.id)}>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  ]}
                >
                  <List.Item.Meta
                    title={<b>{item.name}</b>}
                    description={<Typography.Paragraph ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}>{item.text}</Typography.Paragraph>}
                  />
                </List.Item>
              )}
            />
          </Modal>

          {/* Save New Caption Modal */}
          <Modal
            title="Save Caption"
            open={isSaveCaptionModalOpen}
            onOk={confirmSaveCaption}
            onCancel={() => setIsSaveCaptionModalOpen(false)}
          >
            <Form layout="vertical">
              <Form.Item label="Caption Name" required>
                <Input
                  value={newCaptionName}
                  onChange={(e) => setNewCaptionName(e.target.value)}
                  placeholder="e.g., Morning Motivation, Sales Pitch..."
                />
              </Form.Item>
              <p style={{ fontSize: 12, color: '#888' }}>The current text in the caption box will be saved.</p>
            </Form>
          </Modal>

          {/* Edit Caption Modal */}
          <Modal
            title="Edit Saved Caption"
            open={isEditCaptionModalOpen}
            onOk={saveEditedCaption}
            onCancel={() => setIsEditCaptionModalOpen(false)}
          >
            {editingCaption && (
              <Form layout="vertical">
                <Form.Item label="Name" required>
                  <Input
                    value={editingCaption.name}
                    onChange={(e) => setEditingCaption({ ...editingCaption, name: e.target.value })}
                  />
                </Form.Item>
                <Form.Item label="Text" required>
                  <TextArea
                    rows={4}
                    value={editingCaption.text}
                    onChange={(e) => setEditingCaption({ ...editingCaption, text: e.target.value })}
                  />
                </Form.Item>
              </Form>
            )}
          </Modal>

        </div>

      </div>
    </DndProvider>
  );
}